export enum ErrorCode {
  BadRequest = 400,
  Unauthorized = 401,
  ApiForbidden = 403,
  NotFound = 404,
  Geoblock = 406,
  InternalServerError = 500,
  ServiceUnavailable = 503,
  ServerTimeout = 504,
  MVPDEntitlementError = 1000,
  EntitlementError = 1001,
  MVPDEntitlementFavoriteError = 1002,
  MVPDWithNoEntitlements = 1003,
  InactiveSubscription = 1016,
  CheckSubscription = 1017,
  RecordingNotFound = 1014,
  AccountBlacklisted = 1006,
  UserGeoblocked = 1010,
  ExceededLimit = 1013,
  InvalidSKU = 1015,
}

export type ErrorScope = 'fullscreen' | 'player';

export type ErrorAction =
  | 'reload'
  | 'back'
  | 'back-non-player'
  | 'home'
  | 'signIn'
  | 'help'
  | 'manageSub'
  | 'subscribeUrl';

interface ErrorConfig {
  description: string;
  title: string;
  message: string;
  positiveCta: string;
  positiveCtaAction: ErrorAction;
  negativeCta: string | boolean;
  negativeCtaAction: ErrorAction;
  linkSierraCta: boolean;
  scope: ErrorScope;
}

export const errorConfig: Record<ErrorCode, ErrorConfig> = {
  [ErrorCode.BadRequest]: {
    description: 'Bad Request',
    title: 'Oops!',
    message: "We couldn't load your request. Let's try that again.",
    positiveCta: 'Try Again',
    positiveCtaAction: 'reload',
    negativeCta: 'Dismiss',
    negativeCtaAction: 'back',
    linkSierraCta: false,
    scope: 'fullscreen',
  },
  [ErrorCode.Unauthorized]: {
    description: 'Unauthorized',
    title: "Let's get you signed in!",
    message: "To view this, you'll need to first sign in. Don't worry, we'll hold your spot.",
    positiveCta: 'Sign In',
    positiveCtaAction: 'signIn',
    negativeCta: 'Not Now',
    negativeCtaAction: 'back',
    linkSierraCta: false,
    scope: 'fullscreen',
  },
  [ErrorCode.ApiForbidden]: {
    description: 'API Forbidden',
    title: 'Hmm, we hit a wall.',
    message: 'Sorry, you must be authorized to access this content.',
    positiveCta: 'Try Again',
    positiveCtaAction: 'reload',
    negativeCta: 'Go Back',
    negativeCtaAction: 'back',
    linkSierraCta: false,
    scope: 'fullscreen',
  },
  [ErrorCode.NotFound]: {
    description: 'Title not found',
    title: "Can't find that one.",
    message: "What you're looking for isn't here right now. Can we help you find something else?",
    positiveCta: 'Browse More',
    positiveCtaAction: 'back',
    negativeCta: 'Go Home',
    negativeCtaAction: 'home',
    linkSierraCta: false,
    scope: 'fullscreen',
  },
  [ErrorCode.InternalServerError]: {
    description: 'Internal Server Error',
    title: 'Well, this is awkward.',
    message: "Something broke on our side, but don't worry our team is on it! Try again in a few.",
    positiveCta: 'Try Again',
    positiveCtaAction: 'reload',
    negativeCta: 'Ok',
    negativeCtaAction: 'back',
    linkSierraCta: true,
    scope: 'fullscreen',
  },
  [ErrorCode.ServiceUnavailable]: {
    description: 'Service Unavailable',
    title: "We're taking a quick break.",
    message: 'Our team is doing a little maintenance or hitting some traffic. Please check back shortly.',
    positiveCta: 'Refresh',
    positiveCtaAction: 'reload',
    negativeCta: 'Ok',
    negativeCtaAction: 'back',
    linkSierraCta: true,
    scope: 'fullscreen',
  },
  [ErrorCode.ServerTimeout]: {
    description: 'Server Time-Out',
    title: 'Still waiting...',
    message: "This is taking longer than expected to load. Let's give it another go.",
    positiveCta: 'Retry',
    positiveCtaAction: 'reload',
    negativeCta: 'Not Now',
    negativeCtaAction: 'back',
    linkSierraCta: false,
    scope: 'fullscreen',
  },
  [ErrorCode.EntitlementError]: {
    description: 'Entitlement Error',
    title: "Looks like this isn't included in your subscription.",
    message: "To view this, you'll need access to the content. We can help at fox.com/help",
    positiveCta: 'Visit Help Center',
    positiveCtaAction: 'help',
    negativeCta: 'Dismiss',
    negativeCtaAction: 'back',
    linkSierraCta: true,
    scope: 'player',
  },
  [ErrorCode.InactiveSubscription]: {
    description: 'Inactive Subscription',
    title: 'Inactive Subscription',
    message: "It looks like your subscription is paused. Let's get that restarted!",
    positiveCta: 'Reactivate Now',
    positiveCtaAction: 'manageSub',
    negativeCta: 'Already reactivated? Refresh Now',
    negativeCtaAction: 'reload',
    linkSierraCta: false,
    scope: 'player',
  },
  [ErrorCode.CheckSubscription]: {
    description: 'Check Your Subscription',
    title: 'Check Your Subscription',
    message: "It looks like your subscription is paused. Let's get that restarted!",
    positiveCta: 'Resume Subscription Now',
    positiveCtaAction: 'manageSub',
    negativeCta: 'Already reactivated? Refresh Now',
    negativeCtaAction: 'reload',
    linkSierraCta: false,
    scope: 'player',
  },
  [ErrorCode.RecordingNotFound]: {
    description: 'User Recording Not Found',
    title: 'Recording Not Found',
    message: "Looks like this recording isn't available anymore. Let's find something else to watch.",
    positiveCta: 'Browse Titles',
    positiveCtaAction: 'home',
    negativeCta: 'Dismiss',
    negativeCtaAction: 'back',
    linkSierraCta: false,
    scope: 'player',
  },
  [ErrorCode.AccountBlacklisted]: {
    description: 'Account is Blacklisted',
    title: 'We need to talk.',
    message: 'Your account has been flagged. Visit fox.com/help so we can sort it out.',
    positiveCta: 'Get Help',
    positiveCtaAction: 'help',
    negativeCta: 'Dismiss',
    negativeCtaAction: 'back',
    linkSierraCta: true,
    scope: 'player',
  },
  [ErrorCode.UserGeoblocked]: {
    description: 'User is Geoblocked',
    title: "This isn't available here.",
    message: "You're in a location we don't currently support. Learn more at fox.com/help.",
    positiveCta: 'Retry',
    positiveCtaAction: 'reload',
    negativeCta: 'Dismiss',
    negativeCtaAction: 'back',
    linkSierraCta: true,
    scope: 'player',
  },
  [ErrorCode.ExceededLimit]: {
    description: 'Exceeded Limit of {} Streams',
    title: 'Too many streams.',
    message: "You've got FOX One open on a few devices. Close one to keep watching here.",
    positiveCta: 'Retry',
    positiveCtaAction: 'reload',
    negativeCta: 'Dismiss',
    negativeCtaAction: 'back',
    linkSierraCta: false,
    scope: 'player',
  },
  [ErrorCode.InvalidSKU]: {
    description: 'Invalid SKU',
    title: 'We hit a snag.',
    message: "Something didn't match on our end. If this keeps happening, visit fox.com/help.",
    positiveCta: 'Try Again',
    positiveCtaAction: 'reload',
    negativeCta: 'Dismiss',
    negativeCtaAction: 'back',
    linkSierraCta: true,
    scope: 'player',
  },
  [ErrorCode.Geoblock]: {
    description: 'Geoblock',
    title: 'Not Available in This Region',
    message: 'FoxOne is only available in the U.S. If you feel like this is an error, please try refreshing the app.',
    positiveCta: 'Reload',
    positiveCtaAction: 'reload',
    negativeCta: 'Dismiss',
    negativeCtaAction: 'back',
    linkSierraCta: false,
    scope: 'fullscreen',
  },
  [ErrorCode.MVPDEntitlementError]: {
    description: 'TV Provider',
    title: 'Unlock Free DVR and Personalization',
    message:
      'Create a profile to get personalized recommendations, record events, and save your favorites to My Stuff.',
    positiveCta: 'Continue',
    positiveCtaAction: 'signIn',
    negativeCta: 'Skip',
    negativeCtaAction: 'back-non-player',
    linkSierraCta: false,
    scope: 'fullscreen',
  },
  [ErrorCode.MVPDWithNoEntitlements]: {
    description: 'MVPD With No Entitlements',
    title: 'Subscription Not Found',
    message: "You don't have any active subscription. Please subscribe now.",
    positiveCta: 'Subscribe Now',
    positiveCtaAction: 'subscribeUrl',
    negativeCta: false,
    negativeCtaAction: 'home',
    linkSierraCta: false,
    scope: 'fullscreen',
  },
  [ErrorCode.MVPDEntitlementFavoriteError]: {
    description: 'TV Provider',
    title: 'Unlock Free DVR and Personalization',
    message:
      'Create a profile to get personalized recommendations, record events, and save your favorites to My Stuff.',
    positiveCta: 'Continue',
    positiveCtaAction: 'signIn',
    negativeCta: 'Skip',
    negativeCtaAction: 'back',
    linkSierraCta: false,
    scope: 'fullscreen',
  },
};
